import { Heading, SegmentedTabs, Text } from "@overmap-ai/blocks"
import { type Issue, type IssueType, type IssueTypeStatus, type Stored, EMPTY_ARRAY } from "@overmap-ai/common"
import { selectIssuesOfProject, selectIssueTypesOfOrganization, selectIssueTypeStatuses } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { memo, useCallback, useMemo } from "react"

import { IssueTypeCard } from "../../components/Cards/IssueTypeCard"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { cardLayoutCva, interactiveCardCva } from "../../cva"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

enum IssueTypesViewTabs {
	PROJECT = "project",
	OTHER = "other",
}

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/issue-types/")

export const IssueTypesView = memo(() => {
	const { organizationId, projectId } = routeApi.useParams()

	const issueTypes = useShallowEqaulSelector(selectIssueTypesOfOrganization(organizationId))
	const issues = useShallowEqaulSelector(selectIssuesOfProject(projectId))
	const issueTypeStatuses = useAppSelector(selectIssueTypeStatuses)
	const navigate = useNavigate()

	const [projectIssueTypes, otherIssueTypes] = useMemo(() => {
		const issueTypesWithIssues = new Set(issues.map((issueType) => issueType.issue_type))

		const projectIssueTypes: Stored<IssueType>[] = []
		const otherIssueTypes: Stored<IssueType>[] = []

		for (const issueType of issueTypes) {
			if (issueTypesWithIssues.has(issueType.uuid)) {
				projectIssueTypes.push(issueType)
			} else {
				otherIssueTypes.push(issueType)
			}
		}

		return [projectIssueTypes, otherIssueTypes]
	}, [issueTypes, issues])

	const issuesByIssueType = useMemo(() => {
		const ret: Record<IssueType["uuid"], Stored<Issue>[]> = {}

		for (const issue of issues) {
			if (!(issue.issue_type in ret)) ret[issue.issue_type] = []
			ret[issue.issue_type].push(issue)
		}

		return ret
	}, [issues])

	const issueTypeStatusesByIssueType = useMemo(() => {
		const ret: Record<IssueType["uuid"], Stored<IssueTypeStatus>[]> = {}

		for (const issueTypeStatus of issueTypeStatuses) {
			if (!(issueTypeStatus.issue_type in ret)) ret[issueTypeStatus.issue_type] = []
			ret[issueTypeStatus.issue_type].push(issueTypeStatus)
		}

		return ret
	}, [issueTypeStatuses])

	const handleIssueTypeCardClick = useCallback(
		(issueType: Stored<IssueType>) => {
			void navigate({
				from: "/$organizationId/$projectId/issue-types/",
				to: "/$organizationId/$projectId/issue-types/$issueTypeId",
				params: (prev) => ({ ...prev, issueTypeId: issueType.uuid }),
			})
		},
		[navigate],
	)

	return (
		<ViewContainer maxWidth="6xl">
			<div className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex flex-col gap-1">
					<div className="flex justify-between gap-2">
						<Heading size="xl">Issues</Heading>
					</div>

					<Text size="sm" accentColor="base">
						Issues allow you to track the status, priority and location of work happening in your project.
						You can customize the information you track by creating issue types.
					</Text>
				</div>

				<SegmentedTabs.Root
					defaultValue={IssueTypesViewTabs.PROJECT}
					className="flex size-full flex-col gap-4 overflow-hidden"
				>
					<SegmentedTabs.List size="sm" accentColor="base" variant="surface">
						<SegmentedTabs.Trigger value={IssueTypesViewTabs.PROJECT}>
							Project {projectIssueTypes.length > 0 && `(${projectIssueTypes.length})`}
						</SegmentedTabs.Trigger>
						<SegmentedTabs.Trigger value={IssueTypesViewTabs.OTHER}>
							Other {otherIssueTypes.length > 0 && `(${otherIssueTypes.length})`}
						</SegmentedTabs.Trigger>
					</SegmentedTabs.List>

					<SegmentedTabs.Content
						value={IssueTypesViewTabs.PROJECT}
						className="size-full overflow-y-auto overflow-x-hidden"
					>
						<div className={cardLayoutCva()}>
							{projectIssueTypes.length > 0 ? (
								projectIssueTypes.map((issueType) => (
									<IssueTypeCard
										key={issueType.uuid}
										issueType={issueType}
										issueTypeStatuses={issueTypeStatusesByIssueType[issueType.uuid] ?? EMPTY_ARRAY}
										issues={issuesByIssueType[issueType.uuid] ?? EMPTY_ARRAY}
										onClick={handleIssueTypeCardClick}
										className={cx(interactiveCardCva(), "h-72 cursor-pointer")}
										size="sm"
									/>
								))
							) : (
								<Text accentColor="base" weight="regular" size="sm">
									No issue types.
								</Text>
							)}
						</div>
					</SegmentedTabs.Content>

					<SegmentedTabs.Content
						value={IssueTypesViewTabs.OTHER}
						className="size-full overflow-y-auto overflow-x-hidden"
					>
						<div className={cardLayoutCva()}>
							{otherIssueTypes.length > 0 ? (
								otherIssueTypes.map((issueType) => (
									<IssueTypeCard
										key={issueType.uuid}
										issueType={issueType}
										issueTypeStatuses={issueTypeStatusesByIssueType[issueType.uuid] ?? EMPTY_ARRAY}
										issues={issuesByIssueType[issueType.uuid] ?? EMPTY_ARRAY}
										onClick={handleIssueTypeCardClick}
										className={cx(interactiveCardCva(), "h-72 cursor-pointer")}
										size="sm"
									/>
								))
							) : (
								<Text accentColor="base" weight="regular" size="sm">
									No issue types.
								</Text>
							)}
						</div>
					</SegmentedTabs.Content>
				</SegmentedTabs.Root>
			</div>
		</ViewContainer>
	)
})
IssueTypesView.displayName = "IssueTypesView"
